import { useState } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { DATA_TABLE_SIZE, DataTableColumn, DataTableContextProvider } from 'cpool-components/general/DataTable';
import { APPLICANT_ATTRIBUTION_BOTH, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuRefreshCw } from 'react-icons/lu';

import { AppendCreditForm } from './AppendCreditForm';
import { headers } from './constants';
import { ExpandedItemProvider } from './context/ExpandedItemProvider';
import { CreditList } from './CreditKinds/CreditList';
import { RouteFormLoansModal } from './FormLoansModal/RouteFormLoansModal';
import { KreditTypeSelect } from './KreditTypeSelect';
import { RefinancePlannerNotifications } from './RefinancePlannerNotifications';
import { BankCreditRequestFormSection } from '../BankCreditRequestFormSection';
import LookupFormLoanButton from './SchufaButtons/LookupFormLoanButtons';
import LookupSchufaButton from './SchufaButtons/LookupSchufaButton';
import { discardUnsavedCredit } from './utils/discardUnsavedCredit';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';
import type { LoanType } from '../types';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useCanViewSchufa } from '~/features/leadDetails/hooks/useCanViewSchufa';
import useLead from '~/features/leadDetails/hooks/useLead';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

export const RefinancePlanner: React.FC = () => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const canViewSchufa = useCanViewSchufa(leadId);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { getValues, reset, setValue } = useFormContext<SchemaCreditRequestForm>();

    const [selectedCreditType, setSelectedCreditType] = useState<LoanType | null>(null);

    const canAddForApplicant1 = canExecuteApplicantCommand({
        applicant: 1,
        commandName: LOAN_REQUEST_COMMANDS.addLiabilityInstallmentCredit,
        permissions,
    });
    const canAddForApplicant2 =
        !!lead?.antragsteller2 &&
        canExecuteApplicantCommand({
            applicant: 2,
            commandName: LOAN_REQUEST_COMMANDS.addLiabilityInstallmentCredit,
            permissions,
        });
    const defaultApplicant = canAddForApplicant1
        ? APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1
        : APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2;

    const cancelNewCredit = () => {
        if (selectedCreditType) discardUnsavedCredit({ creditType: selectedCreditType, getValues, reset, setValue });
        setSelectedCreditType(null);
    };

    return (
        <BankCreditRequestFormSection
            title={t('BANK_CREDIT_REQUEST.REFINANCE_PLANNER.TITLE')}
            // headerChildren instead of headerNotification: the amounts stay visible while the planner is open
            headerChildren={<RefinancePlannerNotifications coApplicantHidden={lead?.coApplicantHidden ?? false} />}
            icon={<LuRefreshCw className="size-4" />}
            testId="refinance-planner"
        >
            <ExpandedItemProvider>
                {lead && (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <KreditTypeSelect
                                defaultApplicant={defaultApplicant}
                                selectedCreditType={selectedCreditType}
                                setSelectedCreditType={setSelectedCreditType}
                                disabled={!canAddForApplicant1 && !canAddForApplicant2}
                            />
                            <div className="flex flex-wrap gap-2">
                                <LookupFormLoanButton />
                                {canViewSchufa && <LookupSchufaButton />}
                            </div>
                        </div>
                        {selectedCreditType && (
                            <AppendCreditForm
                                key={selectedCreditType}
                                creditType={selectedCreditType}
                                lead={lead}
                                handleSaveCredit={() => setSelectedCreditType(null)}
                                handleCancel={cancelNewCredit}
                            />
                        )}
                        <RouteFormLoansModal lead={lead} />
                    </div>
                )}
                <DataTableContextProvider size={DATA_TABLE_SIZE.SM}>
                    <div className="@container mt-3 overflow-x-auto rounded-md border border-grey-outline-light bg-white">
                        <table className="w-full table-auto">
                            <thead>
                                <tr>
                                    {headers.map((header) => (
                                        <DataTableColumn
                                            key={header}
                                            field={header}
                                            name={t(`BANK_CREDIT_REQUEST.REFINANCE_PLANNER.TABLE_HEADERS.${header}`)}
                                            className="whitespace-nowrap"
                                        />
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <CreditList />
                            </tbody>
                        </table>
                    </div>
                </DataTableContextProvider>
            </ExpandedItemProvider>
        </BankCreditRequestFormSection>
    );
};
